import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import AnnouncementBanner from '../components/ui/AnnouncementBanner';
import AlertMessage from '../components/ui/AlertMessage';
import Pagination from '../components/ui/Pagination';
import FilterTabs from '../components/ui/FilterTabs';
import ConfirmModal from '../components/ui/ConfirmModal';
import HwidInfoModal from '../components/ui/HwidInfoModal';
import PanelTabs from '../components/ui/PanelTabs';
import { EmptyState } from '../components/ui/PanelCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CopyButton from '../components/ui/CopyButton';
import { roleBadgeClass, ASSIGNABLE_ROLES } from '../constants/roles';
import LeaderboardTab from './admin/LeaderboardTab';
import ReferralsTab from './admin/ReferralsTab';

const USER_FILTERS = [
  { id: '', label: 'All' },
  { id: 'active', label: 'Active sub' },
  { id: 'inactive', label: 'No sub' },
  { id: 'frozen', label: 'Frozen' },
  { id: 'banned', label: 'Banned' },
  { id: 'discord', label: 'Discord linked' },
];

const INVITE_FILTERS = [
  { id: 'pending',  label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all',      label: 'All' },
];

export default function AdminPanel() {
  const { user: me } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState('users');

  // Users tab
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [banModal, setBanModal] = useState(false);
  const [hwidModal, setHwidModal] = useState(false);
  const [banReason, setBanReason] = useState('');

  const [globalFreezeActive, setGlobalFreezeActive] = useState(false);

  // Invites tab
  const [inviteRequests, setInviteRequests] = useState([]);
  const [inviteTotal, setInviteTotal] = useState(0);
  const [inviteFilter, setInviteFilter] = useState('pending');
  const [invitePage, setInvitePage] = useState(1);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [inviteUserSearch, setInviteUserSearch] = useState('');
  const [inviteUserResults, setInviteUserResults] = useState([]);
  const [inviteUserFilter, setInviteUserFilter] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set('search', search);
      if (filter) params.set('filter', filter);
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load users.' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filter]);

  const fetchInviteRequests = useCallback(async () => {
    setInviteLoading(true);
    try {
      const params = new URLSearchParams({ status: inviteFilter, page: invitePage, limit: 15 });
      if (inviteUserFilter) params.set('user_id', inviteUserFilter.id);
      const res = await api.get(`/invite/requests?${params}`);
      setInviteRequests(res.data.requests);
      setInviteTotal(res.data.total);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load invite requests.' });
    } finally {
      setInviteLoading(false);
    }
  }, [inviteFilter, invitePage, inviteUserFilter]);

  const inviteSearchTimer = useRef(null);

  const searchInviteUsers = useCallback((q) => {
    clearTimeout(inviteSearchTimer.current);
    if (!q.trim()) { setInviteUserResults([]); return; }
    inviteSearchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=8`);
        setInviteUserResults(res.data.users || []);
      } catch { setInviteUserResults([]); }
    }, 300);
  }, []);

  const fetchPendingCount = async () => {
    try {
      const res = await api.get('/invite/requests?status=pending&limit=1');
      setPendingCount(res.data.total);
    } catch {}
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchInviteRequests(); }, [fetchInviteRequests]);
  useEffect(() => { fetchPendingCount(); }, []);
  useEffect(() => {
    api.get('/owner/status').then(res => setGlobalFreezeActive(res.data.status === 'updating')).catch(() => {});
  }, []);

  const refreshSelected = async (id) => {
    try {
      const detail = await api.get(`/admin/users/${id}`);
      setSelected(detail.data);
    } catch {}
  };

  const action = async (endpoint, label) => {
    setActionLoading(endpoint);
    setMsg(null);
    try {
      const res = await api.post(endpoint);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      await fetchUsers();
      if (selected) await refreshSelected(selected.id);
    } catch (err) {
      const errMsg = err.response?.data?.error || `Error: ${label}`;
      setMsg({ type: 'error', text: errMsg });
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const banAction = async (userId, reason) => {
    setActionLoading('ban');
    setMsg(null);
    try {
      const res = await api.post(`/admin/users/${userId}/ban`, { reason });
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      await fetchUsers();
      await refreshSelected(userId);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Error banning user.';
      setMsg({ type: 'error', text: errMsg });
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const roleAction = async (userId, newRole) => {
    setActionLoading(`role-${newRole}`);
    setMsg(null);
    try {
      const res = await api.post(`/admin/users/${userId}/set-role`, { role: newRole });
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      await fetchUsers();
      await refreshSelected(userId);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Error changing role.';
      setMsg({ type: 'error', text: errMsg });
      toast.error(errMsg);
    } finally {
      setActionLoading(null);
    }
  };

  const inviteAction = async (requestId, type) => {
    setMsg(null);
    try {
      const res = await api.post(`/invite/requests/${requestId}/${type}`);
      setMsg({ type: 'success', text: res.data.message });
      toast.success(res.data.message);
      await fetchInviteRequests();
      await fetchPendingCount();
    } catch (err) {
      const errMsg = err.response?.data?.error || `Error: ${type}`;
      setMsg({ type: 'error', text: errMsg });
      toast.error(errMsg);
    }
  };

  const selectUser = async (u) => {
    try {
      const detail = await api.get(`/admin/users/${u.id}`);
      setSelected(detail.data);
    } catch { setSelected(u); }
  };

  const pages = Math.ceil(total / 15);
  const isOwner = me?.role === 'owner';

  const hasSub = selected?.expires_at != null;
  const subActive = hasSub && new Date(selected.expires_at) > new Date();
  const subFrozen = selected?.is_frozen === 1;

  return (
    <div className="page-container max-w-7xl">
      <AnnouncementBanner />

      <header className="mb-6">
        <span className="eyebrow mb-3">Staff</span>
        <h1 className="font-display text-2xl font-bold text-white tracking-tight">Admin Panel</h1>
        <p className="text-gray-500 text-sm mt-1.5">
          Signed in as <span className="text-gray-300">{me?.username}</span> ({me?.role})
        </p>
      </header>

      <AlertMessage msg={msg} />

      <PanelTabs
        className="mb-6"
        active={adminTab}
        onChange={setAdminTab}
        tabs={[
          {
            id: 'users',
            label: `Users (${total})`,
            icon: <><path d="M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.9" /></>,
          },
          {
            id: 'invites',
            label: 'Invite Requests',
            count: pendingCount,
            icon: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 7l10 6 10-6" /></>,
          },
          {
            id: 'referrals',
            label: 'Referrals',
            icon: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></>,
          },
          {
            id: 'leaderboard',
            label: 'Leaderboard',
            icon: <><path d="M3 3v18h18" /><path d="M7 15l4-5 3 3 5-7" /></>,
          },
        ]}
      />

      {adminTab === 'referrals' && <ReferralsTab />}
      {adminTab === 'leaderboard' && <LeaderboardTab />}

      {/* USERS TAB */}
      {adminTab === 'users' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="card p-4 space-y-3">
              <div className="relative">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round"
                     className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
                <input type="text" className="input pl-10"
                  placeholder="Search by username, email, HWID or Discord (name, @handle, ID)…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
              <FilterTabs
                filters={USER_FILTERS}
                current={filter}
                onChange={f => { setFilter(f); setPage(1); }}
              />
            </div>

            <div className="card overflow-hidden">
              {loading ? (
                <div className="py-12 flex justify-center"><LoadingSpinner /></div>
              ) : users.length === 0 ? (
                <EmptyState
                  icon={<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>}
                  title="No results found."
                  hint="Try a different search term or filter."
                />
              ) : (
                <div className="scroll-x">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                      <tr>
                        <th className="text-left px-4 py-3 table-head">User</th>
                        <th className="text-left px-4 py-3 table-head hidden sm:table-cell">Role</th>
                        <th className="text-left px-4 py-3 table-head">Status</th>
                        <th className="text-left px-4 py-3 table-head hidden md:table-cell">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id}
                          className={`table-row cursor-pointer ${
                            selected?.id === u.id ? 'bg-brand-500/[0.10] hover:bg-brand-500/[0.12]' : ''
                          }`}
                          onClick={() => selectUser(u)}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-white flex items-center gap-2">
                              {u.username}
                              {u.invited_by && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-500/15
                                                 text-brand-200 border border-brand-400/25">invited</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">{u.email}</div>
                            {/* Kötött Discord: a megjelenített név, mellette az egyedi
                                @handle, ha ismert (a régi kötéseknél még nincs). */}
                            {u.discord_id && (
                              <div className="text-[11px] mt-0.5 flex items-center gap-1.5 text-[#8b93f8]"
                                   title={`Discord ID: ${u.discord_id}`}>
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 flex-shrink-0">
                                  <path d="M20.3 4.4A19.8 19.8 0 0015.4 3l-.6 1.3a18.4 18.4 0 00-5.6 0L8.6 3a19.7 19.7 0 00-4.9 1.4C.6 9 0 13.5.3 17.9a19.9 19.9 0 006 3l1.3-2a12.9 12.9 0 01-2-1l.5-.4a14.2 14.2 0 0012 0l.5.4c-.6.4-1.3.7-2 1l1.3 2a19.8 19.8 0 006-3c.4-5.1-.7-9.6-3.6-13.5zM8.5 15.2c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4zm7 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4z" />
                                </svg>
                                <span className="truncate max-w-[180px]">{u.discord_username}</span>
                                {u.discord_handle && u.discord_handle !== u.discord_username && (
                                  <span className="text-gray-600 truncate">@{u.discord_handle}</span>
                                )}
                              </div>
                            )}
                            {u.hwid && (
                              <div className="text-[11px] text-gray-700 font-mono mt-0.5 truncate max-w-[180px]">
                                {u.hwid}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <RoleBadge role={u.role} />
                          </td>
                          <td className="px-4 py-3">
                            {u.email?.endsWith('@deleted.invalid')
                              ? <span className="badge-inactive">Deleted</span>
                              : u.is_banned ? <span className="badge-banned">Banned</span>
                              : u.is_frozen ? <span className="badge-frozen">Frozen</span>
                              : u.sub_expires && new Date(u.sub_expires) > new Date()
                                ? <span className="badge-active">Active</span>
                                : <span className="badge-inactive">No sub</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs font-mono hidden md:table-cell">
                            {u.sub_expires ? u.sub_expires.slice(0, 10) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <Pagination page={page} totalPages={pages} onPage={setPage} />
          </div>

          {/* User detail panel */}
          <div>
            {selected ? (
              <div className="card p-5 space-y-4 sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-white flex items-center gap-2 flex-wrap">
                      <span className="truncate">{selected.username}</span>
                      {selected.invited_by && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-500/15
                                         text-brand-200 border border-brand-400/25">invited</span>
                      )}
                    </h3>
                    <div className="mt-2"><RoleBadge role={selected.role} /></div>
                  </div>
                  <button onClick={() => setSelected(null)} className="btn-icon -mt-1 -mr-1 flex-shrink-0"
                          aria-label="Close details">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" className="w-4 h-4">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="surface-sunken px-3.5 text-sm">
                  <DetailRow label="Email" value={selected.email} />
                  <DetailRow label="HWID" value={selected.hwid || '—'} mono />
                  {selected.invited_by_name && <DetailRow label="Invited by" value={selected.invited_by_name} />}
                  <DetailRow label="Sub status" value={
                    !hasSub ? 'No subscription' :
                    subFrozen ? 'Frozen' : subActive ? 'Active' : 'Expired'
                  } />
                  {selected.expires_at && <DetailRow label="Expires" value={selected.expires_at.slice(0,16).replace('T',' ')} />}
                  {selected.is_banned === 1 && !selected.email?.endsWith('@deleted.invalid') && <DetailRow label="Ban reason" value={selected.ban_reason || '—'} />}
                  {selected.email?.endsWith('@deleted.invalid') && <DetailRow label="Account" value="Deleted (anonymized)" />}
                  <DetailRow label="ToS accepted" value={selected.tos_accepted_at ? selected.tos_accepted_at.slice(0,16).replace('T',' ') : '—'} />
                  <DetailRow label="Email verified" value={selected.email_verified_at ? selected.email_verified_at.slice(0,16).replace('T',' ') : 'Not verified'} />
                  {/* Discord: a megjelenített név nem egyedi és átírható, ezért áll
                      mellette a @handle és az ID is — utóbbi a biztos azonosító. */}
                  <DetailRow label="Discord" value={
                    selected.discord_id
                      ? <span>
                          {selected.discord_username}
                          {selected.discord_handle && selected.discord_handle !== selected.discord_username && (
                            <span className="text-gray-500"> · @{selected.discord_handle}</span>
                          )}
                        </span>
                      : <span className="text-gray-600">Not linked</span>
                  } />
                  {selected.discord_id && (
                    <DetailRow label="Discord ID" value={
                      <span className="inline-flex items-center gap-1.5">
                        {selected.discord_id}
                        <CopyButton value={selected.discord_id} variant="icon" title="Copy Discord ID" />
                      </span>
                    } mono />
                  )}
                  {selected.discord_linked_at && (
                    <DetailRow label="Discord linked" value={selected.discord_linked_at.slice(0,16).replace('T',' ')} />
                  )}
                  <DetailRow label="Referral code" value={selected.referral_code || '—'} mono />
                  {selected.referred_by_name && <DetailRow label="Referred by" value={selected.referred_by_name} />}
                  {selected.referral_count > 0 && (
                    <DetailRow label="Referrals" value={`${selected.referral_count} (+${selected.referral_days_earned} days)`} />
                  )}
                </div>

                <div className="border-t border-white/[0.06] pt-4 space-y-2">
                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-3">Actions</p>

                  {/* Minden egy helyen: vásárlások, ajánlások, meghívók, farm, eszköz,
                      és ownernek az audit log + ban-napló is. */}
                  <ActionBtn label="Open full profile" color="secondary"
                    onClick={() => navigate(`/admin/users/${selected.id}`)} />

                  <ActionBtn label="HWID Info" color="secondary"
                    onClick={() => setHwidModal(true)} />

                  <ActionBtn label="Reset HWID" color="secondary"
                    loading={actionLoading === `/admin/users/${selected.id}/reset-hwid`}
                    onClick={() => action(`/admin/users/${selected.id}/reset-hwid`, 'HWID reset')} />

                  {/* Vésztartalék: ha a usernek nem tud kimenni a megerősítő levél */}
                  {!selected.email_verified_at && !selected.email?.endsWith('@deleted.invalid') && (
                    <ActionBtn label="Verify Email Manually" color="secondary"
                      loading={actionLoading === `/admin/users/${selected.id}/verify-email`}
                      onClick={() => action(`/admin/users/${selected.id}/verify-email`, 'Email verified')} />
                  )}

                  {subFrozen && (
                    <ActionBtn label="Unfreeze Subscription" color="secondary"
                      loading={actionLoading === `/admin/users/${selected.id}/unfreeze`}
                      disabled={globalFreezeActive}
                      title={globalFreezeActive ? 'Global freeze is active — use Global Unfreeze in the Owner Panel.' : undefined}
                      onClick={() => action(`/admin/users/${selected.id}/unfreeze`, 'Unfreeze')} />
                  )}
                  {!subFrozen && subActive && (
                    <ActionBtn label="Freeze Subscription" color="secondary"
                      loading={actionLoading === `/admin/users/${selected.id}/freeze`}
                      onClick={() => action(`/admin/users/${selected.id}/freeze`, 'Freeze')} />
                  )}

                  {!selected.email?.endsWith('@deleted.invalid') && (
                    selected.is_banned === 1 ? (
                      <ActionBtn label="Unban User" color="secondary"
                        loading={actionLoading === `/admin/users/${selected.id}/unban`}
                        onClick={() => action(`/admin/users/${selected.id}/unban`, 'Unban')} />
                    ) : (
                      selected.role !== 'owner' && (selected.role !== 'admin' || isOwner) && (
                        <ActionBtn label="Ban User" color="danger"
                          loading={actionLoading === 'ban'}
                          onClick={() => { setBanReason(''); setBanModal(true); }} />
                      )
                    )
                  )}

                  {isOwner && selected.role !== 'owner' && selected.id !== me.id && (
                    <div className="pt-3 mt-1 border-t border-white/[0.06]">
                      <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-3">
                        Owner actions — role
                      </p>
                      {/* Három állapot, ezért konkrét célszerepkört küldünk, nem
                          "give/remove"-ot: a tester bevezetésével az utóbbi
                          kétértelművé vált. A jelenlegi rang gombja ki van kapcsolva. */}
                      <div className="space-y-2">
                        {ASSIGNABLE_ROLES.map(r => {
                          const current = selected.role === r.id;
                          return (
                            <button
                              key={r.id}
                              onClick={() => roleAction(selected.id, r.id)}
                              disabled={current || actionLoading === `role-${r.id}`}
                              className={`w-full flex items-center justify-between gap-3 px-3.5 py-2 rounded-lg
                                          border text-left transition-all duration-200 ease-smooth
                                          disabled:cursor-not-allowed ${
                                current
                                  ? `${roleBadgeClass(r.id)} opacity-100`
                                  : 'bg-white/[0.03] border-white/[0.08] text-gray-300 hover:text-white hover:border-white/[0.16] hover:bg-white/[0.06]'
                              }`}
                            >
                              <span className="min-w-0">
                                <span className="block text-sm font-medium">
                                  {actionLoading === `role-${r.id}` ? '…' : r.label}
                                </span>
                                <span className="block text-[11px] text-gray-600 mt-0.5">{r.hint}</span>
                              </span>
                              {current && (
                                <span className="text-[10px] font-semibold uppercase tracking-wider flex-shrink-0">
                                  current
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card sticky top-24">
                <EmptyState
                  icon={<><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" /></>}
                  title="Click a user to view details"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {banModal && selected && (
        <ConfirmModal
          title={`Ban ${selected.username}?`}
          confirmLabel="Ban"
          danger
          input={{ label: 'Reason (optional)', placeholder: 'e.g. Cheat detected', value: banReason, onChange: setBanReason }}
          onConfirm={() => { setBanModal(false); banAction(selected.id, banReason); }}
          onCancel={() => setBanModal(false)}
        />
      )}

      {hwidModal && selected && (
        <HwidInfoModal user={selected} onClose={() => setHwidModal(false)} />
      )}

      {/* INVITES TAB */}
      {adminTab === 'invites' && (
        <div className="space-y-4">
          <div className="relative">
            {inviteUserFilter ? (
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm
                              bg-brand-500/[0.08] border border-brand-400/25">
                <span className="text-brand-200 font-medium">{inviteUserFilter.username}</span>
                <span className="text-gray-600 text-xs truncate">{inviteUserFilter.email}</span>
                <button
                  onClick={() => { setInviteUserFilter(null); setInviteUserSearch(''); setInvitePage(1); }}
                  className="ml-auto btn-icon w-7 h-7 flex-shrink-0"
                  aria-label="Clear user filter"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" className="w-3.5 h-3.5">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Filter by user…"
                  value={inviteUserSearch}
                  onChange={e => { setInviteUserSearch(e.target.value); searchInviteUsers(e.target.value); }}
                  onBlur={() => setTimeout(() => setInviteUserResults([]), 150)}
                  className="input py-2 text-sm"
                />
                {inviteUserResults.length > 0 && (
                  <div className="absolute z-20 mt-1.5 w-full rounded-lg overflow-hidden
                                  bg-gray-900/95 backdrop-blur-xl border border-white/[0.09] shadow-card-lg">
                    {inviteUserResults.map(u => (
                      <button key={u.id}
                        onMouseDown={() => { setInviteUserFilter(u); setInviteUserSearch(''); setInviteUserResults([]); setInvitePage(1); }}
                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left
                                   hover:bg-white/[0.06] transition-colors">
                        <span className="text-sm text-white">{u.username}</span>
                        <span className="text-xs text-gray-600 truncate">{u.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <FilterTabs
              filters={INVITE_FILTERS}
              current={inviteFilter}
              onChange={f => { setInviteFilter(f); setInvitePage(1); }}
            />
            <span className="text-xs text-gray-500 self-center ml-2">{inviteTotal} total</span>
          </div>

          <div className="card overflow-hidden">
            {inviteLoading ? (
              <div className="py-12 flex justify-center"><LoadingSpinner /></div>
            ) : inviteRequests.length === 0 ? (
              <EmptyState
                icon={<><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 7l10 6 10-6" /></>}
                title="No requests found."
              />
            ) : (
              <div className="divide-y divide-white/[0.05]">
                {inviteRequests.map(req => (
                  <div key={req.id} className="p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-white">{req.username}</span>
                          <span className="text-xs text-gray-600">{req.email}</span>
                          {req.has_active_sub ? (
                            <span className="badge-active">Active sub</span>
                          ) : (
                            <span className="badge-inactive">No sub</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 font-mono">
                          {req.created_at?.slice(0, 16).replace('T', ' ')}
                        </p>
                      </div>
                      <StatusBadge status={req.status} />
                    </div>

                    <div className="surface-sunken p-3.5 mb-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                        Reason
                      </p>
                      <p className="text-sm text-gray-300 leading-relaxed">{req.reason}</p>
                    </div>

                    {req.status === 'approved' && req.code && (
                      <div className="rounded-lg p-3.5 mb-3 bg-brand-500/[0.07] border border-brand-400/20">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                          Generated code
                        </p>
                        <div className="flex items-center gap-1">
                          <code className="font-mono text-brand-200 text-sm tracking-widest">{req.code}</code>
                          <CopyButton value={req.code} variant="icon" />
                        </div>
                        {req.used_at && (
                          <p className="text-xs text-gray-600 mt-1.5">
                            Used by {req.used_by_name} on {req.used_at?.slice(0, 10)}
                          </p>
                        )}
                      </div>
                    )}

                    {req.status !== 'pending' && (
                      <p className="text-xs text-gray-600">
                        {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.reviewed_by_name} on {req.reviewed_at?.slice(0, 10)}
                      </p>
                    )}

                    {req.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => inviteAction(req.id, 'approve')}
                          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium
                                     bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300
                                     border border-emerald-500/25 hover:border-emerald-500/45
                                     transition-all duration-200 ease-smooth">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                               strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                          Approve
                        </button>
                        <button
                          onClick={() => inviteAction(req.id, 'reject')}
                          className="btn-danger text-sm py-1.5 px-4">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                               strokeLinecap="round" className="w-3.5 h-3.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Pagination
            page={invitePage}
            totalPages={Math.ceil(inviteTotal / 15)}
            onPage={setInvitePage}
          />
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border
                      ${roleBadgeClass(role)}`}>
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:  'bg-amber-500/12 text-amber-300 border-amber-500/25',
    approved: 'bg-emerald-500/12 text-emerald-300 border-emerald-500/25',
    rejected: 'bg-red-500/12 text-red-300 border-red-500/25',
  };
  return (
    <span className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold capitalize
                      flex-shrink-0 ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-start gap-3 py-2 border-b border-white/[0.05] last:border-0">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-gray-200 text-right break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function ActionBtn({ label, color, loading, disabled, title, onClick }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} title={title}
      className={`w-full py-2 text-sm ${color === 'danger' ? 'btn-danger' : 'btn-secondary'}`}>
      {loading ? '…' : label}
    </button>
  );
}
